import {
  Box,
  Group,
  Loader,
  Modal,
  Progress,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNgpSamplingAssignmentQueries } from '../../data-access/queries/gp-sampling-assignment.queries';
import type {
  NgpRecapLaboratory,
  NgpRecapRow,
} from '../../data-access/types/gp-sampling-assignment';

import { recapPercent } from './group-rows';
import { NgpRecapOrganizationTable } from './organization-table';

type Focus = 'hidden' | 'organic' | 'sms' | 'unassigned';
type Scope = 'fr' | 'world';

function matchesFocus(row: NgpRecapRow, focus: Focus | null): boolean {
  if (focus === 'unassigned') {
    return row.sampler_id == null;
  }
  if (focus === 'hidden') {
    return row.points_visible < row.points_total;
  }
  if (focus === 'sms') {
    return row.sms_sent < row.points_total;
  }
  if (focus === 'organic') {
    return row.organic_pending > 0;
  }
  return true;
}

interface NgpSamplingRecapModalProps {
  onClose: () => void;
  opened: boolean;
}

export const NgpSamplingRecapModal: FC<NgpSamplingRecapModalProps> = ({
  onClose,
  opened,
}) => {
  const { t } = useTranslation();
  const assignmentQueries = useNgpSamplingAssignmentQueries();
  const recapQuery = useQuery({
    ...assignmentQueries.recap(),
    enabled: opened,
  });

  const [search, setSearch] = useState('');
  const [focus, setFocus] = useState<Focus | null>(null);
  const [scope, setScope] = useState<Scope>('fr');

  const i18n = (key: string, options?: Record<string, unknown>) =>
    t(`ngpAssignment.recap.${key}`, options);

  const laboratoryLabel = (laboratory: NgpRecapLaboratory) =>
    laboratory.name ?? i18n('noLaboratory');

  const allRows = useMemo(
    () => recapQuery.data?.[scope] ?? [],
    [recapQuery.data, scope],
  );

  const metrics = useMemo(() => {
    let points = 0;
    let unassigned = 0;
    let hidden = 0;
    let sms = 0;
    let organic = 0;
    let finished = 0;
    let pending = 0;
    let impossible = 0;
    for (const row of allRows) {
      points += row.points_total;
      hidden += row.points_total - row.points_visible;
      sms += row.points_total - row.sms_sent;
      organic += row.organic_pending;
      finished += row.points_finished;
      pending += row.points_pending;
      impossible += row.points_impossible;
      if (row.sampler_id == null) {
        unassigned += row.points_total;
      }
    }
    return { finished, hidden, impossible, organic, pending, points, sms, unassigned };
  }, [allRows]);

  const laboratories = recapQuery.data?.laboratories?.[scope] ?? [];

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const unassignedLabel = t('ngpAssignment.recap.unassignedSampler').toLowerCase();
    return allRows
      .filter((row) => {
        if (!matchesFocus(row, focus)) {
          return false;
        }
        if (!query) {
          return true;
        }
        const name = (row.sampler_name ?? unassignedLabel).toLowerCase();
        const organization = row.organization?.name?.toLowerCase() ?? '';
        return name.includes(query) || organization.includes(query);
      });
  }, [allRows, focus, search, t]);

  const metricItems: { id: Focus | 'points'; label: string; value: number }[] = [
    { id: 'points', label: i18n('metricPoints'), value: metrics.points },
    { id: 'unassigned', label: i18n('metricUnassigned'), value: metrics.unassigned },
    { id: 'hidden', label: i18n('metricHidden'), value: metrics.hidden },
    { id: 'sms', label: i18n('metricSms'), value: metrics.sms },
    { id: 'organic', label: i18n('metricOrganic'), value: metrics.organic },
  ];

  const selectMetric = (id: Focus | 'points') => {
    if (id === 'points') {
      setFocus(null);
      return;
    }
    setFocus((current) => (current === id ? null : id));
  };

  return (
    <Modal
      onClose={onClose}
      opened={opened}
      size="100%"
      styles={{
        body: { maxHeight: 'calc(94vh - 88px)', overflow: 'auto' },
        content: {
          maxHeight: '94vh',
          maxWidth: 'calc(100vw - 32px)',
          width: 'calc(100vw - 32px)',
        },
        header: {
          alignItems: 'center',
          borderBottom: '1px solid var(--mantine-color-gray-2)',
          padding: '18px 20px 14px',
        },
      }}
      title={
        <Stack gap={8}>
          <Text fw={700} lh={1} size="xl">
            {i18n('title')}
          </Text>
          <Box
            style={{
              background: 'var(--mantine-color-pf-green-7)',
              borderRadius: 99,
              height: 3,
              width: 40,
            }}
          />
        </Stack>
      }
    >
      {recapQuery.isLoading ? (
        <Group gap="xs" justify="center" py="xl">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">{i18n('loading')}</Text>
        </Group>
      ) : (
        <Stack gap="md">
          <SegmentedControl
            aria-label={i18n('title')}
            data={[
              { label: i18n('scopeFrance'), value: 'fr' },
              { label: i18n('scopeWorld'), value: 'world' },
            ]}
            onChange={(value) => setScope(value === 'world' ? 'world' : 'fr')}
            value={scope}
          />

          {allRows.length === 0 ? (
            <Text c="dimmed" py="xl" ta="center">{i18n('empty')}</Text>
          ) : (
            <>
              <Group gap={0} grow preventGrowOverflow={false} wrap="wrap">
                {metricItems.map((item) => {
                  const active = item.id === 'points' ? focus == null : focus === item.id;
                  const quiet = item.id !== 'points' && item.value === 0;
                  return (
                    <UnstyledButton
                      aria-pressed={active}
                      disabled={quiet}
                      key={item.id}
                      onClick={() => selectMetric(item.id)}
                      px="sm"
                      py={6}
                      style={{
                        borderBottom: active
                          ? '2px solid var(--mantine-color-pf-green-7)'
                          : '2px solid transparent',
                      }}
                    >
                      <Text
                        c={quiet ? 'dimmed' : undefined}
                        fw={700}
                        lh={1}
                        size="xl"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {item.value}
                      </Text>
                      <Text c="dimmed" mt={4} size="xs">{item.label}</Text>
                    </UnstyledButton>
                  );
                })}
              </Group>

              <Stack gap={6}>
                <Group gap="lg" justify="space-between">
                  <Text c="dimmed" size="xs">{i18n('columnSampled')}</Text>
                  <Group gap="md">
                    <Text fw={700} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {metrics.finished} / {metrics.points}
                    </Text>
                    {metrics.pending > 0 ? (
                      <Text c="dimmed" size="sm">
                        {i18n('sampledPending', { count: metrics.pending })}
                      </Text>
                    ) : null}
                    {metrics.impossible > 0 ? (
                      <Text c="red.7" size="sm">
                        {i18n('sampledImpossible', { count: metrics.impossible })}
                      </Text>
                    ) : null}
                  </Group>
                </Group>
                <Progress
                  aria-label={i18n('columnSampled')}
                  color="green"
                  size="sm"
                  value={recapPercent(metrics.finished, metrics.points)}
                />
              </Stack>

              {laboratories.length > 0 ? (
                <Stack gap={8}>
                  <Text c="dimmed" size="xs">{i18n('laboratoriesTitle')}</Text>
                  <Table.ScrollContainer minWidth={Math.max(420, laboratories.length * 120 + 110)}>
                    <Table withColumnBorders withTableBorder>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th />
                          {laboratories.map((laboratory) => (
                            <Table.Th key={laboratory.id ?? 'none'} ta="right">
                              {laboratoryLabel(laboratory)}
                            </Table.Th>
                          ))}
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        <Table.Tr>
                          <Table.Th scope="row">{i18n('analyses')}</Table.Th>
                          {laboratories.map((laboratory) => (
                            <Table.Td key={laboratory.id ?? 'none'} ta="right">
                              <Text fw={700} size="lg" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {laboratory.samples}
                              </Text>
                            </Table.Td>
                          ))}
                        </Table.Tr>
                        <Table.Tr>
                          <Table.Th scope="row">
                            <Text c="dimmed" fw={500} size="sm">{i18n('metricPoints')}</Text>
                          </Table.Th>
                          {laboratories.map((laboratory) => (
                            <Table.Td key={laboratory.id ?? 'none'} ta="right">
                              <Text c="dimmed" size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {laboratory.points}
                              </Text>
                            </Table.Td>
                          ))}
                        </Table.Tr>
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </Stack>
              ) : null}

              <TextInput
                onChange={(event) => setSearch(event.currentTarget.value)}
                placeholder={i18n('search')}
                value={search}
              />

              {rows.length === 0 ? (
                <Text c="dimmed" size="sm">{i18n('empty')}</Text>
              ) : (
                <NgpRecapOrganizationTable key={scope} rows={rows} search={search} />
              )}
            </>
          )}
        </Stack>
      )}
    </Modal>
  );
};
